import { HttpContext, HttpContextToken } from '@angular/common/http';

import { ReadOptions } from '@app/domain/models/read-options.model';
import { REQUEST_ATTEMPT_TIMEOUT_MS } from './request-timeout.context';

/**
 * Marca POR PETICION: "esta lectura es de fondo; su fallo no se le anuncia al
 * usuario".
 *
 * Es el primer `HttpContext` del repositorio y el unico sitio donde vive el
 * patron: la proxima lectura de fondo REUTILIZA este token en vez de inventar
 * otro mecanismo. El contrato, que es lo que hay que respetar al reutilizarlo:
 *
 * 1. **El valor por defecto es `false`.** Una peticion que no sabe nada del
 *    token se comporta exactamente como siempre. El cambio es aditivo, no un
 *    interruptor global.
 * 2. **La pone el ORIGEN, nunca la ruta.** Quien construye la peticion es el
 *    unico que sabe si alguien esta esperando ese dato en pantalla. La misma
 *    URL, pedida por la pantalla que la va a pintar, sigue avisando cuando
 *    falla — que es justo lo que una lista de URLs excluidas no sabe
 *    distinguir.
 * 3. **Solo suprime el aviso, jamas el error.** El `HttpErrorResponse` sigue
 *    viajando hasta el suscriptor. Marcar una peticion NO es ignorarla: es
 *    hacerse cargo de su fallo en el sitio correcto. Si el llamador no tiene un
 *    `catchError` que lo traduzca a "sin dato", la marca esta mal puesta.
 * 4. **No toca la sesion.** El interceptor la consulta al decidir si abre el
 *    modal generico, no en las ramas de 401/403 de sesion: una lectura de fondo
 *    con el token caducado sigue disparando el refresh como cualquier otra.
 * 5. **Cuando se usa:** la respuesta tiene un estado "sin dato" ya diseñado y
 *    visible, y el usuario no pidio esa lectura. **Cuando NO:** cualquier cosa
 *    que el usuario acaba de pedir, y cualquier escritura.
 *    **Excepcion acotada (feature 92):** el REINTENTO de un bloque que la
 *    pantalla ya pinta. El operador lo pide, pero el aviso se lo da la propia
 *    pantalla en el mismo sitio del clic; sin la marca, el primer fallo sale una
 *    vez y el segundo dos (bloque + modal). Es el caso de `CardsSpeiOriginStore`
 *    (`reload()` pasa por el mismo camino marcado que la lectura inicial). No
 *    se extiende a una accion nueva del usuario sin su propio bloque: la
 *    resolucion de destinos (`…/dispersion/resolve/`) sigue sin marca.
 *    **Excepcion acotada (feature 95, por nodo desde la 116):** `/cards/linked-operations`
 *    (`GET …/<nivel>/<id>/linked-movements/`: lectura de la pagina y paginas de
 *    la descarga). La pide el operador, pero la pantalla
 *    tiene un mensaje VISIBLE propio por cada motivo (403, 404, rango, tope,
 *    red/5xx con Reintentar) y la descarga su aviso: sin la marca cada fallo
 *    salia dos veces. Decision del humano (enmienda 2026-10-01).
 *    **Excepcion acotada (feature 108):** `GET /api/v1/dashboard/global-summary/`
 *    (`GlobalDashboardRemoteDataSource`). El dashboard global pinta su propio
 *    mensaje VISIBLE por motivo (403 sin acceso, 404 todavia no disponible,
 *    red/5xx/tiempo con Reintentar): sin la marca cada fallo saldria dos veces.
 *    Un 401 de sesion vencida sigue abriendo su dialogo (punto 4).
 *    **Excepcion acotada (feature 107):** la bandeja de aprobaciones de Cards
 *    (`GET /api/cards/approval-requests/` y su `count/`,
 *    `CardsApprovalRequestsRemoteDataSource`, marca puesta por la FUENTE). La
 *    bandeja pinta su propio estado por motivo (404 todavia no disponible, 403
 *    sin acceso, red/5xx con Reintentar) y el contador se oculta: sin la marca
 *    cada fallo saldria dos veces. Las decisiones (`…/decision/`, `…/retry/`)
 *    NO van marcadas. Un 401 de sesion vencida sigue abriendo su dialogo.
 *    **Excepcion acotada (feature 119a):** Transacciones de Cards
 *    (`GET /api/cards/<nivel>/<id>/movements/`, cada pagina de la lectura del
 *    nodo; marca puesta por la PANTALLA via `ReadOptions`). La pantalla pinta en
 *    la tarjeta de la tabla el copy de `ErrorCopyService` con Reintentar: sin la
 *    marca cada fallo saldria dos veces. El dashboard de Cards pide la misma ruta
 *    SIN marca. Un 401 de sesion vencida sigue abriendo su dialogo.
 *    **Excepcion acotada (feature 156):** la carga masiva de comisiones TPV
 *    (`GET /api/v1/tpv/<nivel>/tasa/bulk/` y `POST …/tasa/bulk/template/`, marca
 *    puesta por la FUENTE via `ReadOptions`). La plantilla es un POST sin efectos
 *    (genera un archivo) y se trata como lectura. La pantalla pinta su propio aviso
 *    VISIBLE por motivo (403, 404, 429, 502, red con Reintentar): sin la marca cada
 *    fallo saldria dos veces. La CARGA (`POST …/tasa/bulk/`) es una escritura y NO
 *    va marcada. Un 401 de sesion vencida sigue abriendo su dialogo.
 *
 * `HttpContext` es client-side: no genera header, ni query param, ni cuerpo.
 * El backend no se entera de nada.
 */
export const SILENT_BACKGROUND_READ = new HttpContextToken<boolean>(() => false);

/** `HttpContext` nuevo con la marca puesta. */
export function silentBackgroundReadContext(): HttpContext {
  return new HttpContext().set(SILENT_BACKGROUND_READ, true);
}

/**
 * Traduce las `ReadOptions` del dominio a la marca de transporte.
 *
 * Devuelve `undefined` cuando no aplica —y no un `HttpContext` vacio— para que
 * `HttpRequest` construya el suyo como siempre: asi las peticiones normales no
 * pagan un objeto de mas por nada.
 */
export function backgroundReadContext(options?: ReadOptions): HttpContext | undefined {
  const tope = options?.attemptTimeoutMs;
  const conTope = typeof tope === 'number' && Number.isFinite(tope) && tope > 0;
  if (!options?.silent && !conTope) return undefined;

  const context = new HttpContext();
  if (options?.silent) context.set(SILENT_BACKGROUND_READ, true);
  // Feature 152: el tope por intento viaja con la MISMA traduccion, asi que los
  // data sources que ya pasan `backgroundReadContext(options)` lo aplican sin
  // cambiar su codigo. Lo hace cumplir `RequestTimeoutInterceptor`.
  if (conTope) context.set(REQUEST_ATTEMPT_TIMEOUT_MS, tope);
  return context;
}
